import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import AIPromptDialog from "../components/AIPromptDialog";
import WordCount from "../components/WordCount";
import { exportCoverLetterToPDF, exportCoverLetterToDOCX } from "../lib/coverLetterExporters";
import { toast } from "sonner";
import {
  Loader2, Save, Download, Check, ArrowLeft, ArrowRight, FileText, FileType2, Lock, Sparkles, Mail,
} from "lucide-react";

const STEPS = [
  { id: "personal", label: "You" },
  { id: "recipient", label: "Recipient" },
  { id: "role", label: "Role" },
  { id: "draft", label: "Draft" },
  { id: "review", label: "Review" },
];

const DRAFT_QUESTIONS = [
  { key: "why", label: "Why are you interested in this role / company?", placeholder: "e.g. their mission to make legal help accessible matches what I've worked on for 5 years" },
  { key: "strengths", label: "What 2–3 strengths make you a fit?", placeholder: "e.g. led a 6-person team, shipped 3 client products on time, fluent in Spanish & English" },
  { key: "achievements", label: "Specific results you can point to?", placeholder: "e.g. cut onboarding time 40%, trained 12 new hires, A+ accuracy on QA reviews" },
  { key: "fitTo", label: "Anything from the job description you want to address?", placeholder: "e.g. they ask for stakeholder management — I led a 3-team rollout last year" },
  { key: "tone", label: "Tone you want (optional)", placeholder: "e.g. warm and confident; formal; enthusiastic" },
];

function StepHeader({ title, desc, children }) {
  return (
    <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
      <div>
        <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">{title}</h2>
        {desc && <p className="text-sm text-slate-500 mt-1">{desc}</p>}
      </div>
      {children}
    </div>
  );
}

export default function CoverLetterBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [letter, setLetter] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/cover-letters/${id}`);
        setLetter({
          ...data,
          accentColor: data.accentColor || "#0f172a",
          personalInfo: data.personalInfo || {},
          recipient: data.recipient || {},
          role: data.role || "",
          answers: data.answers || {},
          body: data.body || "",
        });
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        nav("/dashboard");
      }
    })();
  }, [id, nav]);

  const persist = useCallback(async (l) => {
    if (!l) return;
    setSaving(true);
    try {
      await api.put(`/cover-letters/${id}`, {
        title: l.title,
        sourceResumeId: l.sourceResumeId || null,
        accentColor: l.accentColor || "#0f172a",
        personalInfo: l.personalInfo || {},
        recipient: l.recipient || {},
        role: l.role || "",
        answers: l.answers || {},
        body: l.body || "",
      });
      setLastSaved(new Date());
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }, [id]);

  const update = (next) => {
    setLetter(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(next), 900);
  };

  const saveNow = async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    await persist(letter);
    toast.success("Saved");
  };

  const downloadPDF = async () => {
    setExporting(true);
    try {
      await persist(letter);
      await exportCoverLetterToPDF(letter, `${letter.title || "cover-letter"}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setExporting(false);
    }
  };

  const downloadDOCX = async () => {
    setExporting(true);
    try {
      await persist(letter);
      await exportCoverLetterToDOCX(letter, `${letter.title || "cover-letter"}.docx`);
      toast.success("DOCX downloaded");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setExporting(false);
    }
  };

  if (!letter) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const stepId = STEPS[stepIdx].id;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="border-b border-slate-200 bg-white sticky top-16 z-30">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Button variant="ghost" size="sm" onClick={() => nav("/dashboard")} className="h-9" data-testid="cl-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <Mail className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <Input
              value={letter.title}
              onChange={(e) => update({ ...letter, title: e.target.value })}
              className="max-w-sm font-display font-semibold border-transparent hover:border-slate-200 focus-visible:border-slate-300"
              data-testid="cl-title-input"
            />
            <div className="hidden md:flex items-center gap-1.5 text-xs text-slate-500" data-testid="cl-save-indicator">
              {saving ? (
                <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>
              ) : lastSaved ? (
                <><Check className="w-3 h-3 text-emerald-500" /> Saved {lastSaved.toLocaleTimeString()}</>
              ) : (
                <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Encrypted</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={saveNow} className="h-9" data-testid="cl-save-btn">
              <Save className="w-4 h-4 mr-1.5" /> Save
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="bg-slate-900 hover:bg-slate-800 h-9" disabled={exporting} data-testid="cl-download-btn">
                  {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1.5" /> Download</>}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={downloadPDF} data-testid="cl-download-pdf">
                  <FileText className="w-4 h-4 mr-2" /> Download PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={downloadDOCX} data-testid="cl-download-docx">
                  <FileType2 className="w-4 h-4 mr-2" /> Download DOCX
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 pb-4">
          <div className="flex items-center justify-between text-xs">
            {STEPS.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setStepIdx(i)}
                className={`flex items-center gap-2 ${i === stepIdx ? "text-blue-600 font-semibold" : i < stepIdx ? "text-slate-700" : "text-slate-400"}`}
                data-testid={`cl-step-tab-${s.id}`}
              >
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-mono-display ${i === stepIdx ? "bg-blue-600 text-white" : i < stepIdx ? "bg-slate-200 text-slate-700" : "bg-slate-100 text-slate-500"}`}>
                  {i + 1}
                </span>
                <span className="hidden sm:inline">{s.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          {stepId === "personal" && <PersonalStep letter={letter} update={update} />}
          {stepId === "recipient" && <RecipientStep letter={letter} update={update} />}
          {stepId === "role" && <RoleStep letter={letter} update={update} />}
          {stepId === "draft" && (
            <DraftStep
              letter={letter}
              update={update}
              onOpenAI={() => setAiOpen(true)}
            />
          )}
          {stepId === "review" && (
            <ReviewStep
              letter={letter}
              update={update}
              onDownloadPDF={downloadPDF}
              onDownloadDOCX={downloadDOCX}
              exporting={exporting}
            />
          )}

          <div className="mt-8 flex justify-between">
            <Button variant="outline" onClick={() => setStepIdx(Math.max(0, stepIdx - 1))} disabled={stepIdx === 0} data-testid="cl-prev-btn">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
            </Button>
            {stepIdx < STEPS.length - 1 ? (
              <Button onClick={() => setStepIdx(stepIdx + 1)} className="bg-slate-900 hover:bg-slate-800" data-testid="cl-next-btn">
                Continue <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            ) : (
              <Button onClick={downloadPDF} className="bg-blue-600 hover:bg-blue-700" data-testid="cl-finish-download-btn">
                <Download className="w-4 h-4 mr-1.5" /> Download PDF
              </Button>
            )}
          </div>
        </div>
      </main>

      <AIPromptDialog
        open={aiOpen}
        onOpenChange={setAiOpen}
        title="Generate cover letter draft"
        description="Tell me what makes you a fit. I'll use only what you write — no made-up details."
        questions={DRAFT_QUESTIONS}
        action="cover_letter"
        role={letter.role || ""}
        context=""
        onResult={(t) => {
          // Merge persisted answers as well (optional)
          update({ ...letter, body: t });
        }}
      />
    </div>
  );
}

function PersonalStep({ letter, update }) {
  const pi = letter.personalInfo || {};
  const set = (k, v) => update({ ...letter, personalInfo: { ...pi, [k]: v } });
  const fields = [
    ["fullName", "Full name", "Jordan Rivera"],
    ["email", "Email", "jordan@example.com"],
    ["phone", "Phone", "+1 555 0100"],
    ["location", "Location", "San Francisco, CA"],
  ];
  return (
    <div>
      <StepHeader title="Your info" desc="This appears at the top of the cover letter.">
        <span className="text-xs text-emerald-700 inline-flex items-center gap-1 bg-emerald-50 px-2 py-1 rounded-md border border-emerald-100">
          <Lock className="w-3 h-3" /> Encrypted on save
        </span>
      </StepHeader>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {fields.map(([k, l, ph]) => (
          <div key={k}>
            <Label className="text-sm">{l}</Label>
            <Input
              value={pi[k] || ""}
              onChange={(e) => set(k, e.target.value)}
              placeholder={ph}
              className="mt-1.5"
              data-testid={`cl-personal-${k}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function RecipientStep({ letter, update }) {
  const r = letter.recipient || {};
  const set = (k, v) => update({ ...letter, recipient: { ...r, [k]: v } });
  return (
    <div>
      <StepHeader title="Who's it addressed to?" desc="Optional — if you don't know the hiring manager's name, we'll use 'Dear Hiring Manager.'" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm">Hiring manager name</Label>
          <Input value={r.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Alex Chen" className="mt-1.5" data-testid="cl-recipient-name" />
        </div>
        <div>
          <Label className="text-sm">Their title</Label>
          <Input value={r.title || ""} onChange={(e) => set("title", e.target.value)} placeholder="Director of Operations" className="mt-1.5" data-testid="cl-recipient-title" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-sm">Company</Label>
          <Input value={r.company || ""} onChange={(e) => set("company", e.target.value)} placeholder="Acme Logistics" className="mt-1.5" data-testid="cl-recipient-company" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-sm">Company address (optional)</Label>
          <Textarea rows={3} value={r.address || ""} onChange={(e) => set("address", e.target.value)} placeholder="123 Market St&#10;Denver, CO 80202" className="mt-1.5" data-testid="cl-recipient-address" />
        </div>
      </div>
    </div>
  );
}

function RoleStep({ letter, update }) {
  return (
    <div>
      <StepHeader title="What role are you applying for?" desc="A clear title helps the AI write a more relevant draft." />
      <Label className="text-sm">Job title</Label>
      <Input
        value={letter.role || ""}
        onChange={(e) => update({ ...letter, role: e.target.value })}
        placeholder="e.g. Senior Operations Lead"
        className="mt-1.5"
        data-testid="cl-role-input"
      />
    </div>
  );
}

function DraftStep({ letter, update, onOpenAI }) {
  return (
    <div>
      <StepHeader title="Write the letter" desc="Generate a draft from your notes, then edit freely.">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 text-xs gap-1 border-blue-200 text-blue-700 bg-blue-50/50 hover:bg-blue-50 hover:text-blue-800"
          onClick={onOpenAI}
          data-testid="cl-ai-draft-btn"
        >
          <Sparkles className="w-3 h-3" /> Generate with AI
        </Button>
      </StepHeader>
      <Label className="text-sm">Letter body</Label>
      <Textarea
        rows={16}
        value={letter.body || ""}
        onChange={(e) => update({ ...letter, body: e.target.value })}
        placeholder="Write your cover letter here, or click 'Generate with AI' above to start from your notes."
        spellCheck
        className="mt-1.5 font-serif text-[15px] leading-relaxed"
        data-testid="cl-body-textarea"
      />
      <WordCount text={letter.body || ""} suggestedMax={320} />
      <p className="text-xs text-slate-500 mt-3">
        Tip: Aim for 3 short paragraphs. The salutation, date, and signature are added automatically when you download.
      </p>
    </div>
  );
}

function ReviewStep({ letter, update, onDownloadPDF, onDownloadDOCX, exporting }) {
  const pi = letter.personalInfo || {};
  const r = letter.recipient || {};
  const today = new Date().toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  const salutation = r.name ? `Dear ${r.name.split(" ")[0]},` : "Dear Hiring Manager,";

  return (
    <div>
      <StepHeader title="Review & download" desc="Read it once more, then download as PDF or DOCX." />
      <div className="rounded-xl border border-slate-200 bg-white p-8 font-serif text-[15px] leading-relaxed text-slate-800 shadow-sm" data-testid="cl-preview">
        <div className="border-b pb-4 mb-4" style={{ borderColor: letter.accentColor || "#0f172a" }}>
          <div className="font-display font-bold text-xl tracking-tight" style={{ color: letter.accentColor || "#0f172a" }}>
            {pi.fullName || "Your name"}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {[pi.email, pi.phone, pi.location].filter(Boolean).join("  •  ")}
          </div>
        </div>
        <div className="text-sm text-slate-600 mb-4">{today}</div>
        {(r.name || r.title || r.company || r.address) && (
          <div className="text-sm text-slate-700 mb-5 whitespace-pre-line">
            {[r.name, r.title, r.company, r.address].filter(Boolean).join("\n")}
          </div>
        )}
        <div className="mb-4">{salutation}</div>
        <div className="whitespace-pre-wrap">
          {letter.body?.trim() || <span className="text-slate-400 italic">Your letter body will appear here.</span>}
        </div>
        <div className="mt-6">Sincerely,</div>
        <div className="mt-6 font-semibold">{pi.fullName || ""}</div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        <Button onClick={onDownloadPDF} disabled={exporting} className="bg-slate-900 hover:bg-slate-800" data-testid="cl-review-download-pdf">
          <FileText className="w-4 h-4 mr-1.5" /> Download PDF
        </Button>
        <Button onClick={onDownloadDOCX} disabled={exporting} variant="outline" data-testid="cl-review-download-docx">
          <FileType2 className="w-4 h-4 mr-1.5" /> Download DOCX
        </Button>
      </div>
    </div>
  );
}
